import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlashMark } from "@/components/Logo";
import { api, mediaUrl } from "@/lib/api";
import { themeOf, sortEvents } from "@/lib/themes";
import { ChevronLeft, ChevronRight, Grid3x3, Maximize, X } from "lucide-react";

export default function Present() {
  const { username, slug } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [tl, setTl] = useState(null);
  const [err, setErr] = useState(false);
  const [i, setI] = useState(0);
  const [overview, setOverview] = useState(false);

  useEffect(() => {
    (async () => {
      const tid = params.get("tid");
      try {
        if (tid) {
          try { const { data } = await api.get(`/timelines/${tid}`); setTl(data); return; } catch {}
        }
        const { data } = await api.get(`/public/${username}/${slug}`); setTl(data);
      } catch { setErr(true); }
    })();
  }, [username, slug, params]);

  const events = sortEvents(tl?.events || []);
  const t = themeOf(tl?.theme);

  const next = useCallback(() => setI((v) => Math.min(events.length - 1, v + 1)), [events.length]);
  const prev = useCallback(() => setI((v) => Math.max(0, v - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); overview ? setOverview(false) : next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { if (overview) setOverview(false); else if (document.fullscreenElement) document.exitFullscreen(); else nav(-1); }
      else if (e.key.toLowerCase() === "f") { document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.(); }
      else if (e.key.toLowerCase() === "o") { setOverview((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, overview, nav]);

  if (err) return <div className="fixed inset-0 grid place-items-center bg-black text-white/80">Timeline not available for presentation.</div>;
  if (!tl) return <div className="fixed inset-0 grid place-items-center" style={{ background: t.bg }}><div className="animate-pulse"><SlashMark size={40} color={t.accent} /></div></div>;

  const ev = events[i];

  return (
    <div className="fixed inset-0 overflow-hidden grain" style={{ background: t.bgGradient }} data-testid="present-mode">
      {/* Slide */}
      <AnimatePresence mode="wait">
        {!overview && ev && (
          <motion.div key={ev.id} initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.99 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0">
            {ev.image && (
              <>
                <img src={mediaUrl(ev.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: t.dark ? "linear-gradient(to top, rgba(6,4,16,0.94), rgba(6,4,16,0.5))" : "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2))" }} />
              </>
            )}
            <div className="relative z-10 h-full grid place-items-center px-8 sm:px-20 text-center">
              <div className="max-w-4xl">
                <div className="text-sm font-semibold uppercase tracking-[0.35em] mb-6" style={{ color: ev.image ? "#fff" : t.accent }}>{ev.date}</div>
                <h1 className="text-4xl sm:text-7xl font-semibold leading-[1.02]" style={{ fontFamily: t.heading, color: ev.image ? "#fff" : t.text }}>{ev.title}</h1>
                {ev.subtitle && <p className="mt-5 text-xl sm:text-2xl italic" style={{ color: ev.image ? "rgba(255,255,255,0.85)" : t.muted }}>{ev.subtitle}</p>}
                {ev.description && <p className="mt-6 text-base sm:text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: ev.image ? "rgba(255,255,255,0.92)" : t.muted }}>{ev.description}</p>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overview */}
      {overview && (
        <div className="absolute inset-0 z-20 overflow-y-auto p-8 sm:p-16" style={{ background: t.bg }}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-semibold mb-8" style={{ fontFamily: t.heading, color: t.text }}>{tl.title}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {events.map((e, idx) => (
                <button key={e.id} onClick={() => { setI(idx); setOverview(false); }} data-testid={`overview-slide-${idx}`}
                  className="text-left rounded-xl border overflow-hidden transition-transform hover:-translate-y-1" style={{ borderColor: t.surfaceBorder, background: t.surface }}>
                  {e.image && <img src={mediaUrl(e.image)} alt="" className="w-full h-24 object-cover" />}
                  <div className="p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: t.accent }}>{e.date}</div>
                    <div className="text-sm font-medium mt-0.5 line-clamp-2" style={{ color: t.text }}>{e.title}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chrome */}
      <div className="absolute top-5 left-6 z-30 flex items-center gap-2" style={{ color: t.text }}>
        <SlashMark size={16} color={t.accent} /><span className="text-sm font-medium opacity-80">{tl.title}</span>
      </div>
      <div className="absolute top-5 right-6 z-30 flex items-center gap-2">
        <IconBtn t={t} onClick={() => setOverview((o) => !o)} testid="present-overview"><Grid3x3 size={18} /></IconBtn>
        <IconBtn t={t} onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.()}><Maximize size={18} /></IconBtn>
        <IconBtn t={t} onClick={() => nav(-1)} testid="present-exit"><X size={18} /></IconBtn>
      </div>

      {!overview && (
        <>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4">
            <IconBtn t={t} onClick={prev} disabled={i === 0} testid="present-prev"><ChevronLeft size={20} /></IconBtn>
            <span className="text-sm tabular-nums" style={{ color: t.text }}>{i + 1} / {events.length}</span>
            <IconBtn t={t} onClick={next} disabled={i === events.length - 1} testid="present-next"><ChevronRight size={20} /></IconBtn>
          </div>
          <div className="absolute bottom-0 left-0 h-1 z-30 transition-all" style={{ width: `${((i + 1) / events.length) * 100}%`, background: t.accent }} />
        </>
      )}
    </div>
  );
}

const IconBtn = ({ children, onClick, disabled, t, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="glass h-10 w-10 grid place-items-center rounded-full disabled:opacity-30 transition-transform hover:scale-105"
    style={{ color: t.text }}>{children}</button>
);
